import {useAccount, useMsal} from '@azure/msal-react';
import {FC, useCallback, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import PersonIcon from '@mui/icons-material/Person';
import IconButton from '@mui/material/IconButton';
import classNames from 'classnames';
import {useAppSelector} from '../../store/hooks';
import {RootState} from '../../store/store';
import {currentUserProps} from '../../store/currentUser/currentUser.slice';
import {logoutUser} from '../../api/general';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {PASSWORD_CHANGE_REQUEST} from '../../services/auth-provider';
import routes from '../user/routes';
import {useSnackbarHook} from '../../hooks/useSnackbar';
import Spinner from '../common/Spinner';

const popupLabels = {
  title: 'Please confirm your action.',
  body: 'Changes you made may not be saved.',
  confirmBtn: 'Confirm',
  cancelBtn: 'Cancel'
};

export const UserMenu: FC = () => {
  const history = useHistory();
  const {t} = useTranslation('user');
  const {isDirtyForm} = useAppSelector(
      (state: RootState) => state.navMenu);
  const {instance, accounts} = useMsal();
  const account = useAccount(accounts[0]);
  const [isPopupShown, setIsPopupShown] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [isChangePasswordLoading, setIsChangePasswordLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const {addErrorToast} = useSnackbarHook();

  const isLoading = isChangePasswordLoading || isLogoutLoading;

  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    if (isLoading) return;

    setAnchorEl(null);
  }, [isLoading]);

  const currentUser: currentUserProps = useAppSelector(
      (state: RootState) => state.user
  );
  const {firstName, lastName, email} = currentUser || {};

  const handlePasswordChange = useCallback(async () => {
    setIsChangePasswordLoading(true);

    try {
      await instance.loginRedirect(PASSWORD_CHANGE_REQUEST);
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsChangePasswordLoading(false);
      handleClose();
    }
  }, [handleClose]);

  const handleLogout = useCallback(async () => {
    setIsLogoutLoading(true);
    try {
      await logoutUser();
      await instance.logoutRedirect({
        postLogoutRedirectUri: `${window.location.href}`
      });
    } catch (e) {
      addErrorToast(e?.message);
    } finally {
      setIsLogoutLoading(false);
      handleClose();
    }
  }, [handleClose]);

  const showConfirmationPopup = useCallback(() => {
    if (isDirtyForm) {
      setIsPopupShown(true);
    } else {
      handleLogout();
    }
  }, [isDirtyForm, handleLogout]);

  const navigateToProfile = useCallback(() => {
    history.push(routes.userProfile());
    handleClose();
  }, [handleClose]);

  return (
    <div className="user-menu">
      <IconButton
        classes={{
          root: classNames('icon-button', {'active': Boolean(anchorEl)})
        }}
        onClick={handleOpen}
      >
        <PersonIcon fontSize="medium" className="icon" />
      </IconButton>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal
        classes={{
          paper: 'user-menu__list'
        }}
      >
        <div className="user-menu__details">
          <p className="user-menu__name">
            {firstName && lastName ? `${firstName} ${lastName}` : account?.name}
          </p>
          <p className="user-menu__email">
            {email || account?.idTokenClaims?.['email']}
          </p>
        </div>
        <Divider />
        <MenuItem disabled={isLoading}>
          <ListItemText onMouseDown={navigateToProfile}>
            {t('userDropdown.items.profile')}
          </ListItemText>
        </MenuItem>
        <MenuItem disabled={isLoading}>
          <ListItemText onMouseDown={handlePasswordChange}>
            {t('userDropdown.items.changePassword')}
          </ListItemText>
          {isChangePasswordLoading && (
            <Spinner isLoading={isLoading} embedded />
          )}
        </MenuItem>
        <MenuItem disabled={isLoading}>
          <ListItemText onMouseDown={showConfirmationPopup}>
            {t('userDropdown.items.logout')}
          </ListItemText>
          {isLogoutLoading && (
            <Spinner isLoading={isLoading} embedded />
          )}
        </MenuItem>
      </Menu>

      <ConfirmationPopup
        labels={popupLabels}
        isOpen={isPopupShown}
        setIsOpen={setIsPopupShown}
        confirmableAction={handleLogout}
      />
    </div>
  );
};
